import { DebugLogger } from '@pique/debug';


const logger = new DebugLogger('affine:tauri-auth');

// Token cache for synchronous access
let cachedToken: string | null = null;
let tokenCachePromise: Promise<string | null> | null = null;

function sanitizeTelemetryMessage(message?: string | null): string | undefined {
  if (!message) return undefined;
  return message
    .replace(/https?:\/\/\S+/g, '[url]')
    .replace(/Bearer\s+[^\s]+/gi, 'Bearer [redacted]')
    .replace(/[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '[jwt]')
    .slice(0, 200);
}

function getSafeErrorProperties(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: sanitizeTelemetryMessage(error.message),
    };
  }

  return {
    errorName: typeof error,
    errorMessage: sanitizeTelemetryMessage(String(error)),
  };
}

function captureTauriAuthDiagnostic(
  _event: string,
  _properties?: Record<string, unknown>
) {
  // no-op
}

async function getCachedToken(): Promise<string | null> {
  if (cachedToken) {
    return cachedToken;
  }

  if (!tokenCachePromise) {
    logger.debug('No token cache promise, creating new one');
    tokenCachePromise = (async () => {
      try {
        const { commands: authCommands } = await import(
          '../../../tauri/plugins/auth'
        );

        // Try to get the token with retries to handle initialization timing
        let token: string | null = null;
        let lastError: unknown = null;
        let attempts = 0;
        const maxAttempts = 3;

        while (attempts < maxAttempts && !token) {
          try {
            token = await authCommands.getFromVault('remote-server');
            if (token) {
              logger.debug('Retrieved token from vault');
              lastError = null;
              break;
            }
          } catch (e) {
            lastError = e;
            logger.debug(
              `Attempt ${attempts + 1} failed to get token from vault:`,
              e
            );
          }

          attempts++;
          if (attempts < maxAttempts && !token) {
            // Wait a bit for auth service to initialize
            await new Promise(resolve => setTimeout(resolve, 100 * attempts));
          }
        }

        if (!token && lastError) {
          captureTauriAuthDiagnostic('tauri_auth_token_cache_failed', {
            operation: 'get_from_vault',
            vaultKey: 'remote-server',
            attempts,
            ...getSafeErrorProperties(lastError),
          });
        }

        cachedToken = token;
        return token;
      } catch (e) {
        logger.debug('Failed to get token from vault after all attempts', e);
        captureTauriAuthDiagnostic('tauri_auth_token_cache_failed', {
          operation: 'get_cached_token',
          ...getSafeErrorProperties(e),
        });
        return null;
      }
    })();
  }

  return tokenCachePromise;
}

// Function to invalidate cache and force a refresh
export async function refreshTauriAuthToken(): Promise<void> {
  logger.debug('Refreshing Tauri auth token cache');
  cachedToken = null;
  tokenCachePromise = null;
  await getCachedToken();
}

export async function addTauriAuthHeaders(
  headers: HeadersInit = {}
): Promise<HeadersInit> {
  if (!environment.isTauri) {
    return headers;
  }

  try {
    const token = await getCachedToken();
    if (token) {
      logger.debug('Adding Authorization header from vault token');
      return {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
    }

    logger.debug('No token available, returning original headers');
    return headers;
  } catch (error) {
    logger.debug('Error adding auth headers:', error);
    return headers;
  }
}

export async function addTauriAuthToUrl(url: URL): Promise<URL> {
  if (!environment.isTauri) {
    return url;
  }

  const token = await getCachedToken();
  if (token) {
    url.searchParams.set('token', token);
  }

  return url;
}

export function getCachedTokenSync(): string | null {
  return cachedToken;
}

export function addTauriAuthToUrlSynchronously(urlString: string): string {
  if (!environment.isTauri) {
    return urlString;
  }

  const url = new URL(urlString);

  // Use cached token if available
  if (cachedToken) {
    url.searchParams.set('token', cachedToken);
  } else {
    logger.debug('No cached token for sync call, starting background fetch');
    // Start caching process for next time
    getCachedToken().catch(console.error);
  }

  return url.toString();
}

if (environment.isTauri) {
  getCachedToken().catch(console.error);
}

export async function isProductionDesktop(): Promise<boolean> {
  if (!environment.isTauri) return false;
  try {
    const { getIdentifier } = await import('@tauri-apps/api/app');
    return (await getIdentifier()) === 'com.pique.app';
  } catch {
    return false;
  }
}

export async function signInOauthViaLocalServer(
  serverUrlPrefix: string,
  provider: string,
  options?: { forceConsent?: boolean }
): Promise<void> {
  const { commands: authCommands, events: authEvents } = await import(
    '../../../tauri/plugins/auth'
  );

  let port: number | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  try {
    port = await authCommands.startOauthServer();
    captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
      phase: 'oauth_server_started',
      provider,
      forceConsent: Boolean(options?.forceConsent),
    });

    const authPromise = new Promise<void>((resolve, reject) => {
      timeoutHandle = setTimeout(() => {
        captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
          phase: 'oauth_timeout',
          provider,
        });
        reject(new Error('OAuth timeout - please try again'));
      }, 120000);

      authEvents.authEvent
        .once(event => {
          if (timeoutHandle) clearTimeout(timeoutHandle);
          if (event.payload === 'success') {
            captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
              phase: 'oauth_event_received',
              provider,
              outcome: 'success',
            });
          } else if (
            event.payload &&
            typeof event.payload === 'object' &&
            'error' in event.payload
          ) {
            captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
              phase: 'oauth_event_received',
              provider,
              outcome: 'error',
              eventError: sanitizeTelemetryMessage(
                (event.payload as { error: string }).error
              ),
            });
          } else {
            captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
              phase: 'oauth_event_received',
              provider,
              outcome: 'unknown',
            });
          }

          if (event.payload === 'success') {
            resolve();
          } else if (
            event.payload &&
            typeof event.payload === 'object' &&
            'error' in event.payload
          ) {
            reject(new Error((event.payload as { error: string }).error));
          } else {
            resolve();
          }
        })
        .then(() => {
          captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
            phase: 'oauth_event_listener_registered',
            provider,
          });
        })
        .catch(err => {
          if (timeoutHandle) clearTimeout(timeoutHandle);
          captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow_failed', {
            phase: 'oauth_event_listener_registration',
            provider,
            ...getSafeErrorProperties(err),
          });
          reject(err);
        });
    });

    const url = new URL(`${serverUrlPrefix}/oauth/login`);
    url.searchParams.set('provider', provider);
    url.searchParams.set('redirect_uri', `http://127.0.0.1:${port}/callback`);
    url.searchParams.set('client', 'desktop');
    url.searchParams.set('scopes', 'calendar');
    if (options?.forceConsent) {
      url.searchParams.set('force_consent', '1');
    }

    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url.toString());
    captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
      phase: 'browser_opened',
      provider,
    });
    await authPromise;
    captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
      phase: 'oauth_completed',
      provider,
    });
    await refreshTauriAuthToken();
    captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow', {
      phase: 'token_cache_refreshed',
      provider,
    });
  } catch (error) {
    captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow_failed', {
      phase:
        port === undefined ? 'start_oauth_server_or_preopen' : 'oauth_local_flow',
      provider,
      ...getSafeErrorProperties(error),
    });
    throw error;
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
    if (port !== undefined) {
      authCommands.stopOauthServer(port).catch(error => {
        captureTauriAuthDiagnostic('tauri_auth_oauth_local_flow_failed', {
          phase: 'stop_oauth_server',
          provider,
          ...getSafeErrorProperties(error),
        });
      });
    }
  }
}
